import { useCallback, useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { searchHotels } from 'tripPlanner/api'
import * as HotelSearchKeys from 'tripPlanner/hooks/reactQueryKeys/hotelSearch'
import { mapToDrawerHotels } from './mapToDrawerHotel'
import type { DrawerHotel } from './types'

const EMPTY_HOTELS: Array<DrawerHotel> = []

const HOTEL_SEARCH_STALE_TIME = 5 * 60 * 1000

const INITIAL_LIMIT = 9
const LOAD_MORE_LIMIT = 5

interface UseHotelSearchOptions {
  searchQuery: string
  placeId?: string
  region?: string
  checkIn?: string
  checkOut?: string
  enabled: boolean
}

export default function useHotelSearch({
  searchQuery,
  placeId,
  region,
  checkIn,
  checkOut,
  enabled,
}: UseHotelSearchOptions) {
  const queryResult = useInfiniteQuery({
    queryKey: HotelSearchKeys.search(searchQuery, region, checkIn, checkOut, placeId),
    queryFn: ({ pageParam = 0 }) => {
      const limit = pageParam === 0 ? INITIAL_LIMIT : LOAD_MORE_LIMIT
      return searchHotels({ query: searchQuery, placeId, region, checkIn, checkOut, offset: pageParam, limit })
    },
    getNextPageParam: (lastPage, allPages) => {
      const isFirstPage = allPages.length === 1
      const limit = isFirstPage ? INITIAL_LIMIT : LOAD_MORE_LIMIT
      if (lastPage.length < limit) return undefined
      const totalFetched = allPages.reduce((sum, page) => sum + page.length, 0)
      return totalFetched
    },
    enabled: enabled && !!searchQuery,
    staleTime: HOTEL_SEARCH_STALE_TIME,
  })

  const hotels = useMemo(() => {
    if (!queryResult.data?.pages) return EMPTY_HOTELS
    const allHotels = queryResult.data.pages.flatMap((page) => mapToDrawerHotels(page))
    // Deduplicate by offerId
    const seen = new Set<string>()
    return allHotels.filter((h) => {
      if (seen.has(h.offerId)) return false
      seen.add(h.offerId)
      return true
    })
  }, [queryResult.data])

  const loadMore = useCallback(() => {
    if (queryResult.hasNextPage && !queryResult.isFetchingNextPage) {
      queryResult.fetchNextPage()
    }
  }, [queryResult])

  return {
    hotels,
    isLoading: queryResult.isLoading && queryResult.fetchStatus !== 'idle',
    isError: queryResult.isError,
    hasMore: !!queryResult.hasNextPage,
    isLoadingMore: queryResult.isFetchingNextPage,
    loadMore,
  }
}
